import { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import PdfViewerModal from "../../utils/PdfViewerModal";
import EditDocumentModal from "./EditDocument";
import { AuthContext } from "../../Context/AuthContext";
import Preloader from "../../shared/PreLoaders";
import { Authorization } from "../../Context/Authorization";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const ViewDocument = () => {
  const [documents, setDocuments] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [editModalShow, setEditModalShow] = useState(false);
  const [documentToEdit, setDocumentToEdit] = useState(null);
  const { permissions, selectedJfmcType } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const Api = process.env.REACT_APP_API;
  const [vfcs, setVfcs] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");

  const fetchVfcByJfmcType = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const Config = Authorization();
      const response = await axios.post(`${Api}/api/vfcs/jfmc`, { jfmcTypeId }, Config);
      setVfcs(response.data.vfcs || []);
    } catch (error) {
      console.error("Error fetching VFC data", error);
      toast.error("Failed to load VFCs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedJfmcType) fetchVfcByJfmcType(selectedJfmcType._id);
  }, [selectedJfmcType]);

  const fetchDocuments = async (e) => {
    e.preventDefault();
    if (!selectedVfc) {
      toast.warning("Please select JFMC");
      return;
    }
    const Config = Authorization();
    try {
      setLoading(true);
      const response = await axios.get(`${Api}/api/documents/documents?vfcId=${selectedVfc}`, Config);
      setDocuments(Array.isArray(response.data.documents) ? response.data.documents : []);
    } catch (error) {
      toast.error("Error fetching documents");
    } finally {
      setLoading(false);
    }
  };

  const handleEditDocument = (doc) => {
    setDocumentToEdit(doc);
    setEditModalShow(true);
  };

  const handleSaveEdit = async (updatedDocument) => {
    try {
      await axios.put(`${Api}/api/documents/${updatedDocument._id}`, updatedDocument);
      setDocuments((prevDocuments) =>
        prevDocuments.map((doc) => doc._id === updatedDocument._id ? updatedDocument : doc)
      );
      toast.success("Document updated successfully");
      setEditModalShow(false);
    } catch (error) {
      console.error("Error updating document", error);
      toast.error("Error updating document");
    }
  };

  const handleDeleteDocument = async (id) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        const Config = Authorization();
        await axios.delete(`${Api}/api/documents/documents/${id}`, Config);
        setDocuments((prevDocuments) => prevDocuments.filter((doc) => doc._id !== id));
        toast.success("Document deleted successfully");
      } catch (error) {
        console.error("Error deleting document", error);
        toast.error("Error deleting document");
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const thStyle = { padding: "10px 12px", fontSize: 12, fontWeight: 700, color: "#2D3748", background: "#F7FAFC", border: "1px solid #E2E8F0", textAlign: "left" };
  const tdStyle = { padding: "9px 12px", fontSize: 12, color: "#4A5568", border: "1px solid #E2E8F0" };

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Documents</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>Document</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>View Documents</span>
        </div>
      </div>

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Select JFMC</span>
            </div>
            <form onSubmit={fetchDocuments}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "flex-end" }}>
                <div>
                  <label style={labelStyle}>JFMC Name</label>
                  <select value={selectedVfc} onChange={(e) => setSelectedVfc(e.target.value)} style={inputStyle}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                    <option value="">Select JFMC</option>
                    {vfcs.slice().sort((a, b) => a.vfcName.localeCompare(b.vfcName)).map((vfc) => (
                      <option key={vfc._id} value={vfc._id}>{vfc.vfcName}</option>
                    ))}
                  </select>
                </div>
                <button type="submit"
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)", whiteSpace: "nowrap" }}>
                  <i className="mdi mdi-file-check" /> Submit
                </button>
              </div>
            </form>
          </div>

          {/* ── Results Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
              <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
                <i className="mdi mdi-file-multiple" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Document Records</span>
            </div>
            <div style={{ padding: 16, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>SI No</th>
                    <th style={thStyle}>VFC Name</th>
                    <th style={thStyle}>Range</th>
                    <th style={thStyle}>Document Name</th>
                    <th style={thStyle}>Document Type</th>
                    <th style={thStyle}>Document No</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Details</th>
                    <th style={thStyle}>Uploaded Document</th>
                    {permissions.includes("EditDocs") && permissions.includes("DeleteDocs") && (
                      <th style={thStyle}>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, index) => (
                    <tr key={doc._id} style={{ background: index % 2 === 0 ? "#fff" : "#F7FAFC" }}>
                      <td style={tdStyle}>{index + 1}</td>
                      <td style={tdStyle}>{doc.vfcId?.vfcName}</td>
                      <td style={tdStyle}>{doc.rangeId?.rangeName}</td>
                      <td style={tdStyle}>{doc.documentName}</td>
                      <td style={tdStyle}>{doc.documentType}</td>
                      <td style={tdStyle}>{doc.documentNo}</td>
                      <td style={tdStyle}>{formatDate(doc.date)}</td>
                      <td style={tdStyle}>{doc.details}</td>
                      <td style={tdStyle}>
                        {doc.presignedUrl ? (
                          <a href={doc.presignedUrl} target="_blank" rel="noopener noreferrer"
                            style={{ color: "#2E7D32", fontWeight: 600, textDecoration: "none" }}>
                            View Document
                          </a>
                        ) : (
                          <span style={{ color: "#A0AEC0" }}>No document</span>
                        )}
                      </td>
                      {permissions.includes("EditDocs") && permissions.includes("DeleteDocs") && (
                        <td style={tdStyle}>
                          <i className="mdi mdi-pen" style={{ fontSize: "1.4rem", cursor: "pointer", marginRight: 8 }}
                            onClick={() => handleEditDocument(doc)} />
                          <i className="mdi mdi-delete" style={{ fontSize: "1.4rem", cursor: "pointer" }}
                            onClick={() => handleDeleteDocument(doc._id)} />
                        </td>
                      )}
                    </tr>
                  ))}
                  {documents.length === 0 && (
                    <tr>
                      <td colSpan={10} style={{ ...tdStyle, textAlign: "center", color: "#A0AEC0", padding: "32px" }}>
                        No documents found. Select a JFMC and submit to load records.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <PdfViewerModal show={modalShow} onHide={() => setModalShow(false)} pdfUrl={selectedPdf} />
      <EditDocumentModal
        show={editModalShow}
        onHide={() => setEditModalShow(false)}
        document={documentToEdit}
        onSave={handleSaveEdit}
      />
    </div>
  );
};

export default ViewDocument;
